/**
 * API Service for ICG Hannover Website
 * Handles all backend API calls
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${API_URL}/api`;

/**
 * Auth helper - adds JWT Bearer token for admin routes
 */
const getAuthHeaders = (includeJson = true) => {
  const token = localStorage.getItem('admin_token');
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (includeJson) headers['Content-Type'] = 'application/json';
  return headers;
};

/**
 * Bible API (public)
 */
export const bibleApi = {
  getDailyVerse: async (translation = 'BSB') => {
    const response = await fetch(`${API_BASE}/bible/daily-verse?translation=${translation}`);
    if (!response.ok) throw new Error('Failed to fetch daily verse');
    return response.json();
  },

  getVerse: async (book, chapter, verse, translation = 'BSB') => {
    const response = await fetch(
      `${API_BASE}/bible/verse?book=${book}&chapter=${chapter}&verse=${verse}&translation=${translation}`
    );
    if (!response.ok) throw new Error('Failed to fetch verse');
    return response.json();
  }
};

/**
 * Sermons API (read: public, write: admin)
 */
export const sermonsApi = {
  getAll: async (limit = 20, skip = 0) => {
    const response = await fetch(`${API_BASE}/sermons?limit=${limit}&skip=${skip}`);
    if (!response.ok) throw new Error('Failed to fetch sermons');
    return response.json();
  },

  getById: async (id) => {
    const response = await fetch(`${API_BASE}/sermons/${id}`);
    if (!response.ok) throw new Error('Failed to fetch sermon');
    return response.json();
  },

  create: async (sermonData) => {
    const response = await fetch(`${API_BASE}/sermons`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(sermonData)
    });
    if (!response.ok) throw new Error('Failed to create sermon');
    return response.json();
  }
};

/**
 * Events API (read: public, write: admin)
 */
export const eventsApi = {
  getAll: async (activeOnly = true) => {
    const response = await fetch(`${API_BASE}/events?active_only=${activeOnly}`);
    if (!response.ok) throw new Error('Failed to fetch events');
    return response.json();
  },

  create: async (eventData) => {
    const response = await fetch(`${API_BASE}/events`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(eventData)
    });
    if (!response.ok) throw new Error('Failed to create event');
    return response.json();
  },

  update: async (id, eventData) => {
    const response = await fetch(`${API_BASE}/events/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(eventData)
    });
    if (!response.ok) throw new Error('Failed to update event');
    return response.json();
  },

  delete: async (id) => {
    const response = await fetch(`${API_BASE}/events/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to delete event');
    return response.json();
  }
};

/**
 * Contact API (submit: public, read messages: admin)
 */
export const contactApi = {
  submit: async (formData) => {
    const response = await fetch(`${API_BASE}/contact/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData)
    });
    if (!response.ok) throw new Error('Failed to submit contact form');
    return response.json();
  },

  subscribeNewsletter: async (email) => {
    const response = await fetch(`${API_BASE}/contact/newsletter/subscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });
    if (!response.ok) throw new Error('Failed to subscribe to newsletter');
    return response.json();
  }
};
